import {
  Button,
  Checkbox,
  Container,
  Form,
  Link,
  Modal,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { Layout } from "components/Layout/Layout";
import { HARDSHIP_SUPPORT } from "constants/contacts";
import { getRegional } from "library/getRegional";
import { checkFields } from "library/utils";
import { createHardshipApplication } from "library/zendeskAPI/hardshipApplication";
import {
  getIsShortForm,
  hasPersonalLoan,
} from "pages/HardshipApplication/reducer/hardshipSelectors";
import { ReasonsForApplyingAccordion } from "pages/HardshipApplication/Review/ReasonsForApplyingAccordion/ReasonsForApplyingAccordion";
import { YourAccountsAccordion } from "pages/HardshipApplication/Review/YourAccountsAccordion/YourAccountsAccordion";
import { useEffect, useState } from "react";
import { usePageTracking } from "react-event-tracker";
import { useMutation } from "react-query";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import {
  ActionTypes,
  HardshipState,
  ReviewState,
  shortTermRequiredFields,
} from "../reducer/types";
import { PersonalDetailsAccordion } from "./PersonalDetailsAccordion/PersonalDetailsAccordion";

export const Review = () => {
  const { state, dispatch } = useHardshipApplicationContext();
  const { goBack } = useNav();
  const { review } = state;

  const [isOpen, setOpen] = useState(false);

  const { methods, Field } = useBasisForm<ReviewState>({
    defaultValues: review,
  });

  const regionalCode = getRegional("code");
  const isNZ = regionalCode === "NZ";

  const { trackPageView } = usePageTracking({
    page: {
      pageType: "information-page",
      siteSection: "hardship",
      siteSubSection: "review",
      pageTitle: "Review your application",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  const { mutate, isLoading } = useMutation(createHardshipApplication, {
    onSuccess: (id, formValues) => {
      dispatch({ type: ActionTypes.SUBMIT_SUCCESS, ticketId: id });
      // goHome();
    },

    // AF: TODO When zendesk api integration added.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any, { setErrors }: any) => {
      // TODO
      //  dispatch({ type: ActionTypes.SUBMIT_ERROR });
    },
  });

  const handleSubmit = (data: ReviewState) => {
    const isUnchecked = Object.values(data.acknowledge).some((value) => !value);
    if (isUnchecked) {
      setOpen(true);
      return;
    }
    const updatedState: HardshipState = {
      ...state,
      review: data,
      acknowledged: true,
    };

    const fields = Object.keys(updatedState) as (keyof HardshipState)[];

    if (
      getIsShortForm(updatedState) &&
      checkFields(updatedState, ...shortTermRequiredFields)
    ) {
      mutate({
        isProduction: false,
        isNZ,
        isShortTerm: true,
        fields: updatedState,
      });
    } else if (checkFields(updatedState, ...fields)) {
      mutate({
        fields: updatedState,
        isProduction: false,
        isNZ,
        isLongTerm: true,
      });
    }
  };

  const isSuccessfullySubmitted = state.review?.ticketId;

  useEffect(() => {
    if (isSuccessfullySubmitted) {
      trackPageView({
        page: {
          pageType: "information-page",
          siteSection: "hardship",
          siteSubSection: "review",
          pageTitle: "Review your application",
        },
        user: {
          authenticationStatus: "not logged in",
        },
      });
    }
  }, [isSuccessfullySubmitted, trackPageView]);

  if (isSuccessfullySubmitted) {
    return (
      <Layout>
        <Layout.Header>Financial hardship application</Layout.Header>
        <Layout.Main>
          <Stack gap="8">
            <Text as="h2" textStyle="heading4" color="primary.blue.t100">
              We have received your hardship application.
            </Text>
            <Text>Ticket id: {state.review?.ticketId}</Text>
          </Stack>
        </Layout.Main>
      </Layout>
    );
  }

  return (
    <Layout>
      <Layout.Header>Financial hardship application</Layout.Header>
      <Layout.Main>
        <Modal
          open={isOpen}
          onClose={() => setOpen(false)}
          title="We need your consent(s) to proceed"
        >
          <Stack gap="4">
            <Text>
              We’re unable to progress your online Hardship Care application
              unless we have your consent/acknowledgement for each of the terms
              and conditions we’ve listed.
            </Text>
            <Text>
              If you’re not comfortable providing your consent/acknowledgement
              to these terms and conditions, or you have any questions about
              them, please call us on {HARDSHIP_SUPPORT[regionalCode].NUM}{" "}
              {HARDSHIP_SUPPORT[regionalCode].HOURS} to speak to one of our
              Hardship Care specialists – we’re here to help.
            </Text>
          </Stack>
          <Modal.Footer>
            <Container width="100%" width-xs="160">
              <Button
                width="100%"
                variant="primary"
                onClick={() => setOpen(false)}
              >
                Okay
              </Button>
            </Container>
          </Modal.Footer>
        </Modal>
        <Form methods={methods} onSubmit={handleSubmit}>
          <Stack gap="8">
            <Text as="h2" textStyle="heading4">
              Review your application
            </Text>

            <PersonalDetailsAccordion region={regionalCode} />
            <YourAccountsAccordion region={regionalCode} />
            <ReasonsForApplyingAccordion />

            <Stack gap="6">
              <Text color="grey.t75">
                <strong>
                  I acknowledge the following upon submission of my Hardship
                  Care arrangement:
                </strong>
              </Text>

              <Stack gap="4">
                <Field
                  label="Shopping block"
                  name="acknowledge.shoppingBlock"
                  hideLabel
                  optional
                  as={Checkbox}
                >
                  I cannot make further purchases on any credit card held with
                  Latitude Financial Services as a shopping block will be placed
                  on my account. I can apply to re-access my card when my
                  Hardship Care arrangement is successfully completed and three
                  (3) consecutive minimum monthly payments have been made on
                  time.
                </Field>
                <Field
                  label="Debit card agreement"
                  name="acknowledge.debitCardAgreement"
                  hideLabel
                  optional
                  helpText="If you currently have an active direct debit arrangement to pay your Latitude products, by submitting this application for Hardship Care you agree to vary the date and amount in accordance with your application. You authorise Latitude to amend your direct debit agreement to be consistent with your application for Hardship Care."
                  as={Checkbox}
                >
                  By checking this field you agree to the below and our{" "}
                  <Link href="#" newTab>
                    Direct Debit Service Agreement.
                  </Link>
                </Field>
                <Field
                  label="Terms and conditions"
                  name="acknowledge.termsAndConditions"
                  hideLabel
                  optional
                  as={Checkbox}
                >
                  I have read and agree to the important{" "}
                  <Link href="#" newTab>
                    Terms & Conditions Notice
                  </Link>
                </Field>
                <Field
                  label="Privacy policy"
                  name="acknowledge.privacyPolicy"
                  hideLabel
                  optional
                  as={Checkbox}
                >
                  I consent to Latitude collecting my personal information,
                  including any sensitive information disclosed in this Webform
                  and I have read and agree to{" "}
                  <Link href="#" newTab>
                    Latitude’s Privacy Policy
                  </Link>
                  . I agree to receive all communication relating to my request
                  electronically.
                </Field>
                {isNZ && (
                  <Field
                    label="Maintaining payments"
                    name="acknowledge.maintainingPayments"
                    hideLabel
                    optional
                    as={Checkbox}
                  >
                    I understand that if my Hardship Care application is
                    approved and I maintain my payments under the agreed
                    assistance arrangement, my account may be brought up to date
                    if it is overdue with no change in balance.
                  </Field>
                )}
                {isNZ && hasPersonalLoan(state) && (
                  <Field
                    label="Revised loan"
                    name="acknowledge.revisedLoan"
                    hideLabel
                    optional
                    as={Checkbox}
                  >
                    If Gem Finance agrees to extend the term of the loan and/or
                    reduce the interest rate based on the information provided
                    by me, I will be sent a revised proposal of my Personal Loan
                    contract terms (variation disclosure) for review. I
                    acknowledge that if I do not respond back within five (5)
                    days, this revised proposal may be withdrawn, and my
                    Hardship Care application declined. I agree that I will be
                    able to maintain the agreed amended contractual monthly
                    obligations. As a result of the change to the credit
                    contract, all current overdue payments will no longer be
                    treated as being in arrears. I agree that additional
                    interest may be payable to Latitude as a result of this
                    variation. If I have existing credit insurance on this loan,
                    I will contact the insurer, as the cover may not be valid
                    for loan extensions or variations.
                  </Field>
                )}
              </Stack>
              <FormActions>
                <FormActions.Primary width="260">
                  <Button variant="primary" type="submit" loading={isLoading}>
                    Submit
                  </Button>
                </FormActions.Primary>
                <FormActions.Secondary width="160">
                  <Button variant="secondary" onClick={goBack}>
                    Back
                  </Button>
                </FormActions.Secondary>
              </FormActions>
            </Stack>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
